import "../css/main.scss";
import "../atlantic.js";

/**
 * imports
 */
import { createApp } from "vue";
import { createStore } from "vuex";
import {
  optimisticFromDataset,
  optimisticFromProduct,
} from "../utils/optimistic-line";

/**
 * vuex
 * auto-import all modules and prepare shared store
 */
const vuexModules = import.meta.globEager("../vue/store/**/*.js");
const modules = {};

Object.entries(vuexModules).forEach(([path, definition]) => {
  const name = path
    .replace(/\/vue\/store/g, "")
    .replace(/\.(\/|js)/g, "")
    .replace(/^\./g, "")
    .replace(/\s/g, "-");

  modules[name] = definition.default;
});

const store = createStore({
  strict: process.env.NODE_ENV !== "production",
  modules,
});

/**
 * create vue instance function
 */
const createVueApp = () => {
  const app = createApp({});

  app.config.devtools = process.env.NODE_ENV !== "production";

  app.config.ignoredElements = [
    "safe-sticky",
    "product-list",
    "sort-by-popover",
  ];

  /**
   * vue components
   * auto-import all vue components
   */
  const components = import.meta.globEager("../vue/components/**/*.vue");

  Object.entries(components).forEach(([path, definition]) => {
    const component = definition.default;

    const componentName = component.name
      ? component.name
      : path
          .replace(/\/vue\/components/g, "")
          .replace(/\.(\/|vue|js)/g, "")
          .replace(/(\/|-|_|\s)\w/g, (match) => match.slice(1).toUpperCase())
          .replace(/^\./g, "");

    app.component(componentName, definition.default);
  });

  /**
   * vue mixins
   * auto-register all mixins with a 'global' keyword in their filename
   */
  //   const mixins = require.context('./vue/mixins/', true, /.*global.*\.js$/)
  const mixins = import.meta.globEager("./vue/mixins/*.js");

  Object.entries(mixins).forEach(([path, definition]) => {
    app.mixin(definition.default);
  });

  /**
   * vue directives
   * auto-register all directives with a 'global' keyword in their filename
   */
  const directives = import.meta.globEager("./vue/directives/*.js");

  Object.entries(directives).forEach(([path, definition]) => {
    const directive = definition.default;
    app.directive(directive.name, directive.directive);
  });

  /**
   * vue plugins
   * extend with additional features
   */

  app.use(store);

  return app;
};

/**
 * create and mount vue instance(s)
 */
const appElement = document.querySelector("#app");

if (appElement) {
  createVueApp().mount(appElement);
} else {
  const vueElements = document.querySelectorAll("[vue]");
  if (vueElements) vueElements.forEach((el) => createVueApp().mount(el));
}

if (window.__bocces) {
  /**
   * The theme's own buttons (product page, cards, search) add to the cart and
   * then rebuilt the drawer from the server: POST /cart/add.js, then a refresh
   * that fetched /cart.js and re-rendered the mini-cart template before the new
   * line showed up. This hands the add to the store instead, which puts the row
   * on screen straight away and sends the request behind it.
   *
   * `line` is what the row should show until Shopify answers — see
   * `_optimistic` in `vue/store/cart.js`. Without it the add still works, it
   * just waits for the response as before.
   */
  // So the theme's own scripts, which hold Shopify's product JSON already, can
  // build what the row needs without duplicating the rules.
  window.__bocces.optimisticLineFor = optimisticFromProduct;
  window.__bocces.optimisticLineFromButton = optimisticFromDataset;

  window.__bocces.addToCart = (item, line) => {
    const payload = Array.isArray(item) ? item : [item];

    return store.dispatch(
      "cart/addItem",
      payload.map((entry, index) => ({
        ...entry,
        _optimistic: Array.isArray(line)
          ? line[index]
          : index === 0
          ? line
          : undefined,
      }))
    );
  };

  window.__bocces.mountVueInDynamicSection = (el) => {
    if (!el) {
      console.log("no element passed to intialize vue");
      return;
    }
    const vueElements = el.querySelectorAll("[vue]");
    if (vueElements) vueElements.forEach((el) => createVueApp().mount(el));
  };
} else {
  console.log("NO __BOCCES");
}

/**
 * fixes for Shopify sections
 * 1. properly render vue components on user insert in the theme editor
 * 2. reload the current page to rerender async inserted sections with vue components
 *
 * add the 'vue' keyword to the section's wrapper classes if the section uses any vue functionality e.g.:
 * {% schema %}
 * {
 *   "class": "vue-section"
 * }
 * {% endschema %}
 */
if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.classList.value.includes("vue")) {
      createVueApp().mount(event.target);
    }
  });
} else if (!Shopify.designMode && process.env.NODE_ENV === "development") {
  new MutationObserver((mutationsList) => {
    mutationsList.forEach((record) => {
      const vue = Array.from(record.addedNodes).find(
        (node) => node.classList && node.classList.value.includes("vue")
      );
      if (vue) window.location.reload();
    });
  }).observe(document.body, {
    childList: true,
    subtree: true,
  });
}
